import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  Loader,
  Alert,
  Divider,
  Paper,
  ScrollArea,
} from "@mantine/core";
import { IconAlertCircle, IconMail, IconKey } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getAdminApiEndpoint } from "../../config/config";
import { adminHeaders } from "../../services/adminApi";
import { handleGenericError } from "../../utils/errorHandler";

function EmailContentModal({ isOpen, onClose, repId, repName, companyCode }) {
  const { getVerifiedJWT } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailContent, setEmailContent] = useState(null);

  // Fetch email content when modal opens
  const fetchEmailContent = async () => {
    if (!repId) return;

    try {
      setLoading(true);
      setError(null);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/email-content/${repId}`),
        {
          method: "GET",
          headers: adminHeaders(jwt, companyCode),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setEmailContent(result.data || result);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to fetch email content");
      }
    } catch (err) {
      setError("Error fetching email content");
      handleGenericError(err, "Failed to load email content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && repId) {
      fetchEmailContent();
    } else {
      // Reset state when modal closes
      setEmailContent(null);
      setError(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, repId]);

  const handleClose = () => {
    setEmailContent(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        <Text fw={600} size="lg">
          Email Content - {repName}
        </Text>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        {loading && (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text>Loading email content...</Text>
          </Group>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            <Group justify="space-between">
              <Text>{error}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchEmailContent}
                disabled={loading}
              >
                Retry
              </Button>
            </Group>
          </Alert>
        )}

        {emailContent && !loading && !error && (
          <>
            {/* Signup Email Section */}
            <Stack gap="xs">
              <Group gap="xs">
                <IconMail size={20} />
                <Text fw={600} size="md">
                  Signup Email
                </Text>
              </Group>

              {/* Signup Subject */}
              <Stack gap="xs">
                <Text fw={500} size="sm" c="dimmed">
                  Subject:
                </Text>
                <Paper withBorder p="sm" bg="gray.0">
                  <Text size="sm">
                    {emailContent.signup_subject ||
                      "No signup email subject available"}
                  </Text>
                </Paper>
              </Stack>

              {/* Signup HTML Content */}
              <Stack gap="xs">
                <Text fw={500} size="sm" c="dimmed">
                  HTML Content:
                </Text>
                <Paper withBorder p="md" bg="gray.0">
                  <ScrollArea.Autosize mah={200}>
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {emailContent.signup_html ||
                        "No signup email HTML content available"}
                    </Text>
                  </ScrollArea.Autosize>
                </Paper>
              </Stack>
            </Stack>

            <Divider />

            {/* Forgot Password Email Section */}
            <Stack gap="xs">
              <Group gap="xs">
                <IconKey size={20} />
                <Text fw={600} size="md">
                  Forgot Password Email
                </Text>
              </Group>

              {/* Forgot Password Subject */}
              <Stack gap="xs">
                <Text fw={500} size="sm" c="dimmed">
                  Subject:
                </Text>
                <Paper withBorder p="sm" bg="gray.0">
                  <Text size="sm">
                    {emailContent.forgot_password_subject ||
                      "No forgot password email subject available"}
                  </Text>
                </Paper>
              </Stack>

              {/* Forgot Password HTML Content */}
              <Stack gap="xs">
                <Text fw={500} size="sm" c="dimmed">
                  HTML Content:
                </Text>
                <Paper withBorder p="md" bg="gray.0">
                  <ScrollArea.Autosize mah={200}>
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {emailContent.forgot_password_html ||
                        "No forgot password email HTML content available"}
                    </Text>
                  </ScrollArea.Autosize>
                </Paper>
              </Stack>
            </Stack>
          </>
        )}

        <Divider />

        <Group justify="flex-end">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EmailContentModal;
